/* eslint-disable @typescript-eslint/no-explicit-any */
import { Stack, Typography } from '@mui/material';
import { RootState } from 'packages/blu-booking/src/store';
import { Scheduler } from './UI/Scheduler';
import { memo, useMemo } from 'react';
import { useSchedulerData } from './hooks/useSchedulerData';
import { useSelector } from 'react-redux';
import { CalendarLoader } from '../../UI/CalendarLoader/CalendarLoader';
import { selectSoloOrario } from '../../hooks/useSoloOrario';
import { useFinestraOraria } from '../../hooks/useFinestraOraria';
import {
  getAppuntamentiDelleColonne,
  selectAppuntamentiStudioPronti,
} from '../../utility_objects/AppuntamentiGiornalieri';

/**
 * Wrapper del calendario Studio.
 * Usa useSchedulerData per il data fetching e gestisce
 * il rendering condizionale (nessuno studio per il PdV).
 */
export const StudioCalendarInner = memo(
  function StudioCalendarInner(): JSX.Element {
    const { selectedDate, selectedPointOfSale, view } = useSelector(
      (state: RootState) => state.bluBookingStudioCalendar
    );
    // Riga Ore del riquadro Visualizzazione di questo calendario
    const soloOrario = useSelector(selectSoloOrario('studio'));
    const appointments = useSelector(
      (state: RootState) => state.bluBookingaAppointments.appointments
    );
    const appuntamentiPronti = useSelector(selectAppuntamentiStudioPronti);

    const {
      calendarPointOfSale,
      shiftPointOfSale,
      getPointOfSaleStudi,
      isLoading,
      isSuccessCalendarPointOfSale,
    } = useSchedulerData({
      selectedDate,
      selectedPointOfSale,
    });

    const studi = useMemo(() => getPointOfSaleStudi(), [getPointOfSaleStudi]);
    // Solo gli appuntamenti degli studi in colonna: Syncfusion non disegna gli
    // altri, che non devono allargare la finestra
    const appuntamenti = useMemo(
      () =>
        getAppuntamentiDelleColonne(
          appointments,
          'studioId',
          studi.map((studio) => studio.id)
        ),
      [appointments, studi]
    );

    // Qui e non nello Scheduler, che si rimonta a ogni giorno, punto vendita o
    // caricamento degli appuntamenti: la finestra tenuta passa da un montaggio
    // all'altro, senza tornare alle 5-24. Le colonne hanno tutte l'orario del
    // punto vendita: la banda da unire è quella. Verticale e Orizzontale
    // mostrano lo stesso giorno, con la stessa finestra
    const finestra = useFinestraOraria({
      soloOrario,
      conPulsante: !!selectedPointOfSale,
      view,
      selectedDate,
      orari: calendarPointOfSale,
      appuntamenti,
      appuntamentiPronti,
      sfondoPronto: isSuccessCalendarPointOfSale,
    });

    const GetContent = useMemo(() => {
      // Ore «Prenotabili» senza una finestra da mostrare (prima apertura,
      // dati non pronti): il calendario non si monta, resta il loader
      if (isLoading || finestra.inAttesa) {
        return (
          <Stack
            alignItems="center"
            justifyContent="center"
            direction="row"
            flex="1"
            sx={{
              height: '100%',
              width: '100%',
              backgroundColor: 'var(--base-foreground-color) ',
            }}
          />
        );
      }
      if (
        !isLoading &&
        calendarPointOfSale !== undefined &&
        shiftPointOfSale !== undefined &&
        studi.length === 0
      ) {
        return (
          <div style={{ height: '100%' }}>
            <Stack
              alignItems="center"
              justifyContent="center"
              direction="row"
              flex="1"
              sx={{ height: '100%', width: '100%' }}
            >
              <Typography fontWeight="bold">
                Nessuno studio per il punto vendita selezionato
              </Typography>
            </Stack>
          </div>
        );
      }
      return (
        <Scheduler
          calendarPointOfSale={calendarPointOfSale}
          shiftPointOfSale={shiftPointOfSale}
          studi={studi}
          startHour={finestra.startHour}
          endHour={finestra.endHour}
        />
      );
    }, [
      calendarPointOfSale,
      studi,
      isLoading,
      shiftPointOfSale,
      finestra.startHour,
      finestra.endHour,
      finestra.inAttesa,
    ]);

    return (
      <div style={{ height: '100%' }}>
        {GetContent}
        {/* Ore «Prenotabili» e dati non ancora pronti: il loader resta
            aperto anche sopra il calendario montato */}
        <CalendarLoader open={isLoading || finestra.provvisoria} />
      </div>
    );
  }
);
